/**
 * URL filter utilities for storing/retrieving filters in URL parameters
 */

/**
 * Helper to encode filters to a compact URL-safe string
 *
 * @param {Array} filters - Array of filter objects
 * @returns {string} - URL-safe encoded filters
 */
export const encodeFilters = (filters) => {
  if (!filters || filters.length === 0) return '';
  
  // Create a much more compact representation
  const compactFilters = filters.map(filter => {
    // Use a minimal array format [field, operator, value, type, property?]
    const compact = [
      filter.field,
      filter.operator,
      filter.value,
      filter.type
    ];
    
    // Only add property if it's a properties type filter
    if (filter.type === 'properties' && filter.property) {
      compact.push(filter.property);
    }
    
    return compact;
  });
  
  return encodeURIComponent(JSON.stringify(compactFilters));
};

/**
 * Helper to decode compact filters from URL-safe string and regenerate full filter objects
 *
 * @param {string} filterString - URL-safe encoded filters string
 * @returns {Array} - Array of filter objects
 */
export const decodeFilters = (filterString) => {
  if (!filterString) return [];
  try {
    const compactFilters = JSON.parse(decodeURIComponent(filterString));
    
    // Reconstruct full filter objects from compact format
    return compactFilters.map((compact, index) => {
      // Parse the compact array format [field, operator, value, type, property?]
      const [field, operator, value, type, property] = compact;
      
      // Generate a unique ID for each filter
      const id = `url-filter-${Date.now()}-${index}`;
      
      // Build the complete filter object
      const fullFilter = {
        id,
        field,
        label: field, // Use field as label
        operator,
        value,
        type: type || 'string', // Default to string type if not specified
      };
      
      // Handle properties type filters
      if (type === 'properties' && property) {
        fullFilter.property = property;
      }
      
      return fullFilter;
    });
    
  } catch (e) {
    console.error('Error decoding filters from URL', e);
    return [];
  }
};